import React, { useEffect, useState } from 'react';

import { Autocomplete, Button, Chip, Stack, TextField } from '@mui/material';
import { GridFilterInputValueProps } from '@mui/x-data-grid-pro';

import { normaliseIds } from './utils';

type Props = GridFilterInputValueProps & {
  // Several ids as chips; Enter adds a chip, so only the button searches.
  multiple?: boolean;
  limitTags?: number;
};

const toText = (value: unknown) => (typeof value === 'string' ? value : '');
const toIds = (value: unknown) => normaliseIds(value);

// Typing doesn't filter the list; Search (or Enter in a text search) does.
export default function CompetitorOffersSearchFilterInput({
  item,
  applyValue,
  focusElementRef,
  multiple,
  limitTags,
}: Props) {
  const [text, setText] = useState(toText(item.value));
  const [ids, setIds] = useState(toIds(item.value));
  // Typed or pasted but not yet a chip; Search takes it too.
  const [pending, setPending] = useState('');

  useEffect(() => {
    setText(toText(item.value));
    setIds(toIds(item.value));
  }, [item.value]);

  const searchIds = normaliseIds([...ids, pending]);
  const changed = multiple
    ? searchIds.join(' ') !== toIds(item.value).join(' ')
    : text.trim() !== toText(item.value).trim();
  const submit = () => {
    if (!changed) {
      return;
    }
    setPending('');
    applyValue({ ...item, value: multiple ? searchIds : text.trim() });
  };

  return (
    <Stack direction="row" alignItems="flex-end" spacing={1}>
      {multiple ? (
        <Autocomplete
          multiple
          freeSolo
          fullWidth
          options={[]}
          value={ids}
          limitTags={limitTags}
          onChange={(_, value) => setIds(normaliseIds(value))}
          inputValue={pending}
          onInputChange={(_, value, reason) => setPending(reason === 'reset' ? '' : value)}
          renderTags={(value, getTagProps) =>
            value.map((id, index) => <Chip {...getTagProps({ index })} key={id} size="small" label={id} />)
          }
          renderInput={(params) => (
            <TextField
              {...params}
              variant="standard"
              label="Value"
              placeholder="Paste or type, then Enter"
              inputRef={focusElementRef}
            />
          )}
        />
      ) : (
        <TextField
          variant="standard"
          label="Value"
          fullWidth
          value={text}
          onChange={(event) => setText(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              submit();
            }
          }}
          inputRef={focusElementRef}
        />
      )}
      <Button size="small" variant="contained" onClick={submit} disabled={!changed}>
        Search
      </Button>
    </Stack>
  );
}
